import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Grid,
  Chip,
  LinearProgress,
  InputAdornment,
  Tooltip,
  CircularProgress,
  FormHelperText,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Quiz as QuizIcon,
  School as SchoolIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { baseUrl, config, requestID } from "../../common/utils";
import { IonPage, IonContent } from '@ionic/react';

interface Question {
  id: number;
  question_id: string;
  ex_id: string;
  cou_id: string;
  question: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  option_e: string;
  correct_option: string;
  level: string;
  status: string;
  branch_id: number;
  business_id: number;
  created_at?: string;
  updated_at?: string;
}

interface Exam {
  id: number;
  ex_id: string;
  ex_title: string;
  cou_id: string;
  level: string;
  status: string;
}

interface Course {
  id: number;
  cou_id: string;
  cou_name: string;
}

// Static levels
const LEVELS = ['easy', 'medium', 'hard'];

const CBTQuestionsDashboard = () => {
  // State management
  const [exams, setExams] = useState<Exam[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedExam, setSelectedExam] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<Question[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingExams, setLoadingExams] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [csvDialog, setCsvDialog] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' | 'info' | 'warning' 
  });
  
  // Form state
  const [formData, setFormData] = useState({
    id: 0,
    question_id: '',
    ex_id: '',
    cou_id: '',
    question: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    option_e: '',
    correct_option: '',
    level: 'medium',
    status: 'active',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize with real data from API
  useEffect(() => {
    fetchExams();
    fetchCourses();
  }, []);

  // Load questions when exam is selected
  useEffect(() => {
    if (selectedExam) {
      fetchQuestions();
    } else {
      setQuestions([]);
      setFilteredQuestions([]);
    }
  }, [selectedExam]);

  // Filter questions based on search
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredQuestions(questions);
    } else {
      const filtered = questions.filter(q =>
        q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.correct_option.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.level.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredQuestions(filtered);
    }
  }, [searchTerm, questions]);

  // ================= API CALLS =================

  // Fetch all exams
  const fetchExams = async () => {
    setLoadingExams(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}Cbt_Exams/index`, formData, config);
      
      if (response.data.status === true) {
        setExams(response.data.data || []);
      } else {
        showSnackbar('Failed to fetch exams', 'error');
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoadingExams(false);
    }
  };

  // Fetch all courses
  const fetchCourses = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}Cbt_Courses/index`, formData, config);
      
      if (response.data.status === true) {
        setCourses(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Fetch questions for selected exam
  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("ex_id", selectedExam);

      const response = await axios.post(`${baseUrl()}Cbt_Questions/byExam`, formData, config);
      console.log({ response });
      
      if (response.data.status === true) {
        const questionsData = response.data.data || [];
        setQuestions(questionsData);
        setFilteredQuestions(questionsData);
      } else {
        showSnackbar('Failed to fetch questions', 'error');
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Create question
  const createQuestion = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const postData = new FormData();
      postData.append("requestID", requestID());
      postData.append("question_id", formData.question_id);
      postData.append("ex_id", selectedExam);
      postData.append("cou_id", formData.cou_id);
      postData.append("question", formData.question);
      postData.append("option_a", formData.option_a);
      postData.append("option_b", formData.option_b);
      postData.append("option_c", formData.option_c);
      postData.append("option_d", formData.option_d);
      postData.append("option_e", formData.option_e);
      postData.append("correct_option", formData.correct_option);
      postData.append("level", formData.level);
      postData.append("status", formData.status);

      const response = await axios.post(`${baseUrl()}Cbt_Questions/create`, postData, config);
      
      if (response.data.status === true) {
        showSnackbar('Question created successfully!', 'success');
        fetchQuestions();
        handleCloseDialog();
      } else {
        showSnackbar(response.data.message || 'Failed to create question', 'error');
      }
    } catch (error: any) {
      console.error('Error creating question:', error);
      showSnackbar(error.response?.data?.message || 'Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update question
  const updateQuestion = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (!currentQuestion?.id) {
      showSnackbar('Question ID is required for update', 'error');
      return;
    }

    setLoading(true);
    try {
      const postData = new FormData();
      postData.append("requestID", requestID());
      postData.append("id", currentQuestion.id.toString());
      postData.append("question", formData.question);
      postData.append("option_a", formData.option_a);
      postData.append("option_b", formData.option_b);
      postData.append("option_c", formData.option_c);
      postData.append("option_d", formData.option_d);
      postData.append("option_e", formData.option_e);
      postData.append("correct_option", formData.correct_option);
      postData.append("level", formData.level);
      postData.append("status", formData.status);

      const response = await axios.post(`${baseUrl()}Cbt_Questions/update`, postData, config);
      
      if (response.data.status === true) {
        showSnackbar('Question updated successfully!', 'success');
        fetchQuestions();
        handleCloseDialog();
      } else {
        showSnackbar(response.data.message || 'Failed to update question', 'error');
      }
    } catch (error: any) {
      console.error('Error updating question:', error);
      showSnackbar(error.response?.data?.message || 'Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Delete question
  const deleteQuestion = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("id", id.toString());

      const response = await axios.post(`${baseUrl()}Cbt_Questions/delete`, formData, config);
      
      if (response.data.status === true) {
        showSnackbar('Question deleted successfully!', 'success');
        fetchQuestions();
      } else {
        showSnackbar(response.data.message || 'Failed to delete question', 'error');
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ================= HELPER FUNCTIONS =================

  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    
    if (!formData.question.trim()) {
      errors.question = 'Question is required';
    }

    if (!formData.option_a.trim()) {
      errors.option_a = 'Option A is required';
    }

    if (!formData.option_b.trim()) {
      errors.option_b = 'Option B is required';
    }

    if (!formData.correct_option) {
      errors.correct_option = 'Correct answer is required';
    }

    if (!formData.level) {
      errors.level = 'Difficulty level is required';
    }

    return errors;
  };

  const handleOpenDialog = (question: Question | null = null) => {
    if (question) {
      setCurrentQuestion(question);
      setFormData({
        id: question.id,
        question_id: question.question_id,
        ex_id: question.ex_id,
        cou_id: question.cou_id,
        question: question.question,
        option_a: question.option_a,
        option_b: question.option_b,
        option_c: question.option_c,
        option_d: question.option_d,
        option_e: question.option_e,
        correct_option: question.correct_option,
        level: question.level,
        status: question.status,
      });
    } else {
      // Generate new question ID for creation
      const newQuestionId = `ques_id_${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
      const selectedExamObj = exams.find(exam => exam.ex_id === selectedExam);
      
      setCurrentQuestion(null);
      setFormData({
        id: 0,
        question_id: newQuestionId,
        ex_id: selectedExam,
        cou_id: selectedExamObj?.cou_id || '',
        question: '',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        option_e: '',
        correct_option: '',
        level: 'medium',
        status: 'active',
      });
    }
    
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentQuestion(null);
    setFormErrors({});
  };

  const handleCsvDialogOpen = () => setCsvDialog(true);
  const handleCsvDialogClose = () => setCsvDialog(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = () => {
    if (currentQuestion) {
      updateQuestion();
    } else {
      createQuestion();
    }
  };

  // Simple CSV parser function
  const parseCSV = (text: string) => {
    const lines = text.split('\n');
    if (lines.length === 0) return [];
    
    const headers = lines[0].split(',');
    
    return lines.slice(1).filter(line => line.trim()).map(line => {
      const values = line.split(',').map(v => v.trim());
      const row: Record<string, string> = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      return row;
    });
  };

  const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const csvText = e.target?.result as string;
      const parsedData = parseCSV(csvText);
      
      const parsedQuestions = parsedData
        .filter(row => row.Question) // Only rows with questions
        .map((row, index) => ({
          id: 0,
          question_id: `csv-${Date.now()}-${index}`,
          ex_id: selectedExam,
          cou_id: exams.find(exam => exam.ex_id === selectedExam)?.cou_id || '',
          question: row.Question || '',
          option_a: row['Option A'] || '',
          option_b: row['Option B'] || '',
          option_c: row['Option C'] || '',
          option_d: row['Option D'] || '',
          option_e: row['Option E'] || '',
          correct_option: (row['Correct Answer (a/b/c/d/e)'] || 'a').toLowerCase(),
          level: 'medium',
          status: 'active',
          branch_id: 0,
          business_id: 0
        }));
      
      if (parsedQuestions.length > 0) {
        // Bulk create questions via API
        bulkCreateQuestions(parsedQuestions);
      } else {
        showSnackbar('No valid questions found in CSV file', 'warning');
      }
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    
    reader.onerror = () => {
      showSnackbar('Error reading CSV file', 'error');
    };
    
    reader.readAsText(file);
  };

  const bulkCreateQuestions = async (questionsToCreate: Partial<Question>[]) => {
    setLoading(true);
    try {
      // Create each question individually (you might want to create a bulk endpoint)
      let successCount = 0;
      let errorCount = 0;
      
      for (const question of questionsToCreate) {
        try {
          const postData = new FormData();
          postData.append("requestID", requestID());
          postData.append("question_id", question.question_id || '');
          postData.append("ex_id", question.ex_id || '');
          postData.append("cou_id", question.cou_id || '');
          postData.append("question", question.question || '');
          postData.append("option_a", question.option_a || '');
          postData.append("option_b", question.option_b || '');
          postData.append("option_c", question.option_c || '');
          postData.append("option_d", question.option_d || '');
          postData.append("option_e", question.option_e || '');
          postData.append("correct_option", question.correct_option || '');
          postData.append("level", question.level || 'medium');
          postData.append("status", question.status || 'active');

          const response = await axios.post(`${baseUrl()}Cbt_Questions/create`, postData, config);
          
          if (response.data.status === true) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        showSnackbar(`${successCount} questions imported successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`, 
          errorCount > 0 ? 'warning' : 'success');
        handleCsvDialogClose();
        fetchQuestions(); // Refresh the list
      } else {
        showSnackbar('Failed to import questions', 'error');
      }
    } catch (error) {
      console.error('Error in bulk create:', error);
      showSnackbar('Error during bulk import', 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = ['Question', 'Option A', 'Option B', 'Option C', 'Option D', 'Option E', 'Correct Answer (a/b/c/d/e)'];
    const example = ['What is 2+2?', '3', '4', '5', '6', '', 'b'];
    const csvContent = [headers, example]
      .map(row => row.join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'questions_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getLevelColor = (level: string) => {
    switch(level) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'error';
      default: return 'default';
    }
  };

  // Get exam title by ID
  const getExamTitle = (exId: string) => {
    const exam = exams.find(e => e.ex_id === exId);
    return exam ? exam.ex_title : exId;
  };

  // Get course name by ID
  const getCourseName = (couId: string) => {
    const course = courses.find(c => c.cou_id === couId);
    return course ? course.cou_name : couId;
  };

  // Refresh data
  const handleRefresh = () => {
    fetchExams();
    if (selectedExam) {
      fetchQuestions();
    }
  };

  return (
    <IonPage>
    <IonContent>
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <QuizIcon fontSize="large" />
              CBT Questions Management
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 1, opacity: 0.9 }}>
              Manage questions for your exams
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)'
                }
              }}
            >
              Download Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Exam Selection */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'white' }}>Select Exam</InputLabel>
              <Select
                value={selectedExam}
                label="Select Exam"
                onChange={(e) => setSelectedExam(e.target.value)}
                disabled={loadingExams}
                sx={{
                  color: 'white',
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'white',
                  },
                  '.MuiSvgIcon-root': {
                    color: 'white',
                  }
                }}
              >
                <MenuItem value="">
                  <em>Select an exam</em>
                </MenuItem>
                {exams.map((exam) => (
                  <MenuItem key={exam.id} value={exam.ex_id}>
                    {exam.ex_title} ({exam.ex_id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!selectedExam || loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'white' }} />
                  </InputAdornment>
                ),
                sx: {
                  color: 'white',
                  '&::placeholder': { color: 'rgba(255,255,255,0.7)' },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'white',
                  },
                }
              }}
            />
          </Grid>
        </Grid>

        {selectedExam && exams.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={`Exam: ${getExamTitle(selectedExam)}`}
              color="primary" 
              variant="outlined"
              sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}
            />
            <Chip 
              icon={<SchoolIcon />}
              label={`Course: ${getCourseName(exams.find(e => e.ex_id === selectedExam)?.cou_id || '')}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}
            />
          </Box>
        )}
      </Paper>

      {/* Action Buttons */}
      {selectedExam && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" color="text.secondary">
              {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''} found
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={handleCsvDialogOpen}
                disabled={loading}
              >
                Upload CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                disabled={loading}
              >
                Add Question
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Questions Table */}
      {selectedExam && (
        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          {loading && <LinearProgress />}
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="medium">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700, width: '5%' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '25%' }}>QUESTION</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '25%' }}>OPTIONS</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '10%' }}>CORRECT</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '10%' }}>DIFFICULTY</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '10%' }}>STATUS</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '15%' }} align="center">ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQuestions.map((question, index) => (
                  <TableRow key={question.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{index + 1}</TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2">
                        {question.question}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Chip label={`A: ${question.option_a}`} size="small" sx={{ mb: 0.5, mr: 0.5 }} />
                        <Chip label={`B: ${question.option_b}`} size="small" sx={{ mb: 0.5, mr: 0.5 }} />
                        {question.option_c && (
                          <Chip label={`C: ${question.option_c}`} size="small" sx={{ mb: 0.5, mr: 0.5 }} />
                        )}
                        {question.option_d && (
                          <Chip label={`D: ${question.option_d}`} size="small" sx={{ mb: 0.5, mr: 0.5 }} />
                        )}
                        {question.option_e && (
                          <Chip label={`E: ${question.option_e}`} size="small" sx={{ mb: 0.5, mr: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={question.correct_option.toUpperCase()} 
                        color="success" 
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={question.level} 
                        color={getLevelColor(question.level)}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={Number(question.status) === 1 ? 'Active' : 'Inactive'} 
                        color={Number(question.status) === 1 ? 'success' : 'error'}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Edit Question">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog(question)}
                            disabled={loading}
                            sx={{ color: '#10b981' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Question">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => deleteQuestion(question.id)}
                            disabled={loading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {filteredQuestions.length === 0 && !loading && (
            <Box p={3} textAlign="center">
              <Typography color="textSecondary">
                {searchTerm ? 'No questions found matching your search.' : 'No questions found. Add your first question!'}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Add/Edit Question Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          py: 2,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QuizIcon />
            <Typography variant="h6" fontWeight={600}>
              {currentQuestion ? 'Edit Question' : 'Add New Question'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box component="form" sx={{ pt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Question *"
              name="question"
              value={formData.question}
              onChange={handleInputChange}
              margin="normal"
              required
              error={!!formErrors.question}
              helperText={formErrors.question}
              InputProps={{
                readOnly: false,
              }}
              size="small"
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Option A *"
                  name="option_a"
                  value={formData.option_a}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                  error={!!formErrors.option_a}
                  helperText={formErrors.option_a}
                  InputProps={{
                    readOnly: false,
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Option B *"
                  name="option_b"
                  value={formData.option_b}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                  error={!!formErrors.option_b}
                  helperText={formErrors.option_b}
                  InputProps={{
                    readOnly: false,
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Option C"
                  name="option_c"
                  value={formData.option_c}
                  onChange={handleInputChange}
                  margin="normal"
                  InputProps={{
                    readOnly: false,
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Option D"
                  name="option_d"
                  value={formData.option_d}
                  onChange={handleInputChange}
                  margin="normal"
                  InputProps={{
                    readOnly: false,
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Option E"
                  name="option_e"
                  value={formData.option_e}
                  onChange={handleInputChange}
                  margin="normal"
                  InputProps={{
                    readOnly: false,
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" error={!!formErrors.correct_option} size="small">
                  <InputLabel>Correct Answer *</InputLabel>
                  <Select
                    name="correct_option"
                    value={formData.correct_option}
                    onChange={handleSelectChange}
                    label="Correct Answer *"
                    required
                  >
                    <MenuItem value="a">Option A</MenuItem>
                    <MenuItem value="b">Option B</MenuItem>
                    <MenuItem value="c">Option C</MenuItem>
                    <MenuItem value="d">Option D</MenuItem>
                    <MenuItem value="e">Option E</MenuItem>
                  </Select>
                  {formErrors.correct_option && (
                    <FormHelperText>{formErrors.correct_option}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" error={!!formErrors.level} size="small">
                  <InputLabel>Difficulty Level *</InputLabel>
                  <Select
                    name="level"
                    value={formData.level}
                    onChange={handleSelectChange}
                    label="Difficulty Level *"
                  >
                    {LEVELS.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.level && (
                    <FormHelperText>{formErrors.level}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleSelectChange}
                    label="Status"
                  >
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button 
            onClick={handleCloseDialog} 
            variant="outlined"
            color="inherit"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={loading}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            {loading ? 'Processing...' : currentQuestion ? 'Update Question' : 'Create Question'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={csvDialog} onClose={handleCsvDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Questions via CSV</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            CSV format: Question, Option A, Option B, Option C, Option D, Option E, Correct Answer (a/b/c/d/e)
          </Alert>
          <Box sx={{ mt: 2 }}>
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleCsvUpload}
              style={{ display: 'none' }}
              id="csv-upload-input"
            />
            <label htmlFor="csv-upload-input">
              <Box 
                sx={{ 
                  border: '2px dashed #ccc', 
                  p: 4, 
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Click to upload CSV file
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Supports .csv files with proper format
                </Typography>
              </Box>
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCsvDialogClose} disabled={loading}>Cancel</Button>
          <Button onClick={downloadTemplate} startIcon={<DownloadIcon />} disabled={loading}>
            Download Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* No exam selected message */}
      {!selectedExam && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <QuizIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Please select an exam to manage questions
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Choose an exam from the dropdown above to view, add, edit, or delete questions
          </Typography>
        </Paper>
      )}
    </Container>
    </IonContent>
    </IonPage>
  );
};

export default CBTQuestionsDashboard;